import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../Store/Slices/AuthSlice";
import gatewayApi from "../CustomHooks/gatewayAxios";
import { initSession } from "../CustomHooks/cryptoService";
import { reauthWithMpin } from "../CustomHooks/reauthService";
import { describeMpinError } from "../CustomHooks/gatewayErrors";
import { logoutFromGateway } from "../CustomHooks/gatewayLogout";
import { verifyMpin } from "../CustomHooks/mpinFlow";
import { setUnlockListener, unlockDone, unlockCancelled } from "../CustomHooks/unlockCoordinator";

// Asks for the MPIN OVER the page the user is working on when the gateway's crypto session has lapsed (or their IP
// changed). When the MPIN is right the call that failed is repeated by gatewayAxios, so the user's work is not lost and
// the page is never left. Mounted once, at the app root (App.js); /mpin-verify stays as the fallback screen.
function MpinUnlockModal() {
  const dispatch = useDispatch();
  const employeeDetails = useSelector((state) => state.auth.employeeDetails);
  const [request, setRequest] = useState(null);          // { mode } while the modal is open
  const [mpin, setMpin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockedFor, setLockedFor] = useState(0);

  useEffect(() => setUnlockListener(({ mode }) => {
    setRequest({ mode });
    setMpin("");
    setError("");
    setLockedFor(0);
  }), []);

  useEffect(() => {
    if (lockedFor <= 0) return undefined;
    const id = setInterval(() => setLockedFor((n) => Math.max(n - 1, 0)), 1000);
    return () => clearInterval(id);
  }, [lockedFor > 0]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!request) return null;

  const giveUp = async () => {
    unlockCancelled(new Error("logged out"));
    setRequest(null);
    await logoutFromGateway();      // frees this login's slot at the gateway (best effort, short wait); needs the token, so before clear()
    dispatch(logout());
    localStorage.clear();
    window.location.href = "/";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (lockedFor > 0 || busy) return;
    if (mpin.length !== 6) {
      setError("Enter your 6-digit MPIN.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const mid = employeeDetails?.username;
      const token = employeeDetails?.token;
      const result = await verifyMpin({
        mode: request.mode,
        unlock: () => gatewayApi
          .post("/staff/onboard/validate-mpin", { mid, mpin }, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => r.data),
        reauth: () => reauthWithMpin({ mid, mpin, token }),
        initSession,
      });
      if (result.ok) {
        setRequest(null);
        unlockDone();
      } else if (result.lockedForSeconds > 0) {
        setLockedFor(result.lockedForSeconds);
      } else {
        setError(result.message || "Invalid MPIN.");
      }
    } catch (err) {
      const outcome = describeMpinError(err);
      if (outcome.action === "logout") {
        giveUp();                                   // the login itself is no longer valid
        return;
      }
      if (outcome.action === "locked") setLockedFor(outcome.lockoutSeconds);
      else if (outcome.action === "resession") setError("That took a bit long, please try again.");
      else setError(outcome.message);
    } finally {
      setBusy(false);
      setMpin("");
    }
  };

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Enter your MPIN to continue</h2>
          <p className="text-xs text-slate-500 mt-1">
            {request.mode === "reauth"
              ? "Your network changed. Confirm it is you and we will carry on with what you were doing."
              : "You were away for a while. Confirm it is you and we will carry on with what you were doing."}
          </p>
        </div>
        <input
          type="password"
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          value={mpin}
          maxLength={6}
          disabled={lockedFor > 0}
          onChange={(e) => setMpin(e.target.value.replace(/\D/g, ""))}
          placeholder="6-digit MPIN"
          className="w-full py-3 bg-slate-50 border border-slate-200 focus:border-blue-500 rounded-xl text-center text-xl font-bold tracking-[0.6em] outline-none"
        />
        {lockedFor > 0 && (
          <p className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3">
            Too many wrong attempts. Try again in {lockedFor}s.
          </p>
        )}
        {error && lockedFor <= 0 && (
          <p className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3">{error}</p>
        )}
        <button
          type="submit"
          disabled={busy || lockedFor > 0}
          className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl disabled:opacity-60"
        >
          {busy ? "Checking..." : "Continue"}
        </button>
        <button type="button" onClick={giveUp} className="w-full text-xs font-bold text-slate-500 hover:text-slate-800">
          Log out instead
        </button>
      </form>
    </div>
  );
}

export default MpinUnlockModal;
